/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable @typescript-eslint/no-empty-function */
import jwt_decode from "jwt-decode";
import {
  createContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { fetchClient } from "@/lib/unified-api";
import type { components } from "@/lib/unified-api-schema";
import { IUserShow } from "@/services/users/interfaces";
import { setStorageData, clearToken, getStorageData } from "@/utils";

interface AuthContextProps {
  token: string | null;
  login: (token: string) => void;
  logout: () => void;
  user: IUserShow | null;
  getUserToken: () => void;
  companyId: string | undefined | null;
  companyName: string | undefined | null;
  companyRegionId: string | undefined | null;
  companyIsBeta: boolean;
  companyNeighborhoodId: string | null;
  isAuthenticated: () => boolean;
  isMaster: () => boolean;
  isDeliveryman: () => boolean;
  mockCompanyId: string | null;
  setMockCompanyId: (id: string | null) => void;
  mockCompanyRegionId: string | null;
  mockCompanyIsBeta: boolean;
  setMockCompanyIsBeta: (isBeta: boolean) => void;
  setMockCompanyRegionId: (id: string | null) => void;
  isCompanyMaster: () => boolean;
  mockCompanyNeighborhoodId: string | null;
  setMockCompanyNeighborhoodId: (id: string | null) => void;
}

export const AuthContext = createContext<AuthContextProps>(
  {} as AuthContextProps,
);

const jwtSchema = z.object({
  exp: z.number(),
});

export const TOKEN_KEY = "zvcbcve";

type UnifiedUserResponse = components["schemas"]["AuthenticatedUserResponse"];

type DecodedToken = {
  sub?: string;
  companyID?: string;
  roles?: number[];
};

function mapMeToUser(me: UnifiedUserResponse, decoded: DecodedToken): IUserShow {
  return {
    id: me.id || decoded.sub || "",
    email: me.email || "",
    personID: me.id || "",
    personName: me.name || "",
    personCpfCnpj: me.cpf || "",
    companyID: decoded.companyID || me.company?.id || "",
    companyName: me.company?.name || "",
    companyNeighborhoodID: "",
    companyIsBeta: false,
    companyRegionID: undefined,
    UserRoles: (decoded.roles ?? []).map((roleID) => ({ id: "", roleID })),
    addressCity: me.location?.city,
    addressState: me.location?.state,
  };
}

async function fetchCurrentUser(): Promise<IUserShow | null> {
  try {
    const token = getStorageData(TOKEN_KEY);
    const decoded: DecodedToken = token ? jwt_decode(String(token)) : {};

    const me = await fetchClient.GET("/v1/auth/me");

    if (!me.data) return null;

    return mapMeToUser(me.data, decoded);
  } catch {
    return null;
  }
}

export const AuthProvider = (props: { children: ReactNode }) => {
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);
  const [userToken, setUserToken] = useState<IUserShow | null>(null);

  const getUserToken = useCallback(async () => {
    if (!userToken) {
      const token = getStorageData(TOKEN_KEY);

      if (!token) {
        return;
      }

      const currentUser = await fetchCurrentUser();

      if (currentUser) {
        setUserToken(currentUser);
      }
    }
  }, [userToken]);

  const login = async (newToken: string) => {
    setStorageData(TOKEN_KEY, newToken);
    setToken(newToken);

    const currentUser = await fetchCurrentUser();

    if (currentUser) {
      setUserToken(currentUser);
    }
  };

  const isAuthenticated = () => {
    try {
      const token = getStorageData(TOKEN_KEY);

      if (typeof token === "string" && token) {
        const decodedToken = jwt_decode(token);
        const result = jwtSchema.parse(decodedToken);

        if (result.exp * 1000 > Date.now()) {
          return true;
        }
      }

      return false;
    } catch (e) {
      return false;
    }
  };

  const isMaster = () => {
    return userToken?.UserRoles?.some((role) => role.roleID === 1) || false;
  };

  const isDeliveryman = () => {
    return userToken?.UserRoles?.some((role) => role.roleID === 4) || false;
  };

  const isCompanyMaster = () => {
    return userToken?.UserRoles?.some((role) => role.roleID === 2) || false;
  };

  const logout = () => {
    setToken(null);
    clearToken();
    navigate("/");
  };

  const [companyIsBeta, setCompanyIsBeta] = useState<boolean>(false);
  const [companyNeighborhoodId, setCompanyNeighborhoodId] = useState<
    string | null
  >(null);
  const [companyRegionId, setCompanyRegionId] = useState<
    string | undefined | null
  >(undefined);
  const [companyName, setCompanyName] = useState<string | undefined | null>(
    undefined,
  );
  const [companyId, setCompanyId] = useState<string | undefined | null>(
    undefined,
  );

  useEffect(() => {
    if (!userToken) {
      getUserToken();
      return;
    }

    setCompanyIsBeta(userToken.companyIsBeta);
    setCompanyNeighborhoodId(userToken.companyNeighborhoodID);
    setCompanyRegionId(userToken.companyRegionID);
    setCompanyName(userToken.companyName);
    setCompanyId(userToken.companyID);
  }, [userToken]);

  useEffect(() => {
    if (isDeliveryman()) {
      toast.error("Entregadores não podem visualizar o dashboard", {
        id: "error-deliveryman",
      });
      logout();
    }
  }, [isDeliveryman]);

  const [mockCompanyId, setMockCompanyId] = useState<string | null>(null);
  const [mockCompanyRegionId, setMockCompanyRegionId] = useState<string | null>(
    null,
  );
  const [mockCompanyNeighborhoodId, setMockCompanyNeighborhoodId] = useState<
    string | null
  >(null);
  const [mockCompanyIsBeta, setMockCompanyIsBeta] = useState<boolean>(false);

  return (
    <AuthContext.Provider
      value={{
        token,
        login,
        logout,
        user: userToken,
        getUserToken,
        companyId,
        companyName,
        companyRegionId,
        isAuthenticated,
        isMaster,
        isDeliveryman,
        mockCompanyId,
        setMockCompanyId,
        mockCompanyRegionId,
        setMockCompanyRegionId,
        mockCompanyNeighborhoodId,
        setMockCompanyNeighborhoodId,
        mockCompanyIsBeta,
        setMockCompanyIsBeta,
        isCompanyMaster,
        companyIsBeta,
        companyNeighborhoodId,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};
